/* Un campo solo per cercare e scegliere, al posto di "ricerca piu' tendina".
 *
 * Nato il 25 settembre 2026: la prima versione della ricerca metteva una riga
 * "cerca" sopra ogni tendina, cioe' due campi per una scelta sola, e chi li
 * vede la prima volta non capisce se deve scrivere, scegliere o tutti e due.
 * Qui si scrive e sotto compare l'elenco gia' ristretto; si sceglie col clic
 * o con frecce e Invio, e Esc torna alla scelta di prima.
 *
 * Invio dentro l'elenco sceglie e **non** manda il modulo: mandarlo per
 * sbaglio a meta' scelta sarebbe peggio del campo di prima.
 */

import { Fragment, useId, useState, type KeyboardEvent } from "react";
import { trova } from "../dominio";
import { ErroreCampo } from "./ErroreCampo";

export interface Voce {
  valore: string;
  testo: string;
  /** Una riga piu' piccola sotto il testo, per esempio il prezzo. */
  dettaglio?: string;
  /** Tutto cio' su cui si cerca, oltre al testo: codici, partita IVA. */
  cerca?: string;
  /** Un'intestazione sopra la prima voce di ogni gruppo, finche' non si scrive: guida la scelta
   * ("le tue su questo cliente") senza obbligare a cercare. Le voci dello stesso gruppo vanno di seguito. */
  gruppo?: string;
}

const MOSTRATE = 8;

export function CampoCerca({
  id,
  etichetta,
  voci,
  valore,
  cambia,
  segnaposto = "scrivi per cercare",
  disabilitato = false,
  nessuna = "Nessuna voce trovata",
  errore,
  quante = MOSTRATE,
}: {
  id: string;
  etichetta: string;
  voci: Voce[];
  valore: string;
  cambia: (valore: string) => void;
  segnaposto?: string;
  disabilitato?: boolean;
  nessuna?: string;
  errore?: string;
  /** Quante voci mostra l'elenco aperto. */
  quante?: number;
}) {
  const elenco = useId();
  const scelta = voci.find((v) => v.valore === valore);
  const [testo, setTesto] = useState<string | null>(null);
  const [aperto, setAperto] = useState(false);
  const [evidenza, setEvidenza] = useState(0);

  // Mentre non si scrive, il campo mostra la scelta; scrivendo, la ricerca.
  const cercato = testo ?? "";
  const trovate = voci.filter((v) => trova(cercato, v.testo, v.dettaglio ?? "", v.cerca ?? ""));
  const mostrate = trovate.slice(0, quante);

  function scegli(v: Voce) {
    cambia(v.valore);
    setTesto(null);
    setAperto(false);
  }

  function tasti(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAperto(true);
      setEvidenza((i) => Math.min(i + 1, mostrate.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setEvidenza((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && aperto) {
      e.preventDefault();
      const v = mostrate[evidenza];
      if (v) scegli(v);
    } else if (e.key === "Escape") {
      setTesto(null);
      setAperto(false);
    }
  }

  const attiva = aperto && mostrate[evidenza] ? `${elenco}-${evidenza}` : undefined;

  return (
    <div className="campo-cerca">
      <label htmlFor={id}>{etichetta}</label>
      <input
        id={id}
        role="combobox"
        aria-expanded={aperto}
        aria-controls={elenco}
        aria-activedescendant={attiva}
        aria-autocomplete="list"
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore ? `errore-${id}` : undefined}
        autoComplete="off"
        disabled={disabilitato}
        placeholder={segnaposto}
        value={testo ?? scelta?.testo ?? ""}
        onFocus={(e) => {
          e.target.select();
          setAperto(true);
          setEvidenza(0);
        }}
        onChange={(e) => {
          setTesto(e.target.value);
          setAperto(true);
          setEvidenza(0);
        }}
        onBlur={() => {
          setTesto(null);
          setAperto(false);
        }}
        onKeyDown={tasti}
      />
      <ErroreCampo campo={id} messaggio={errore} />
      {scelta?.dettaglio && testo === null && <p className="aiuto">{scelta.dettaglio}</p>}
      {aperto && !disabilitato && (
        <ul id={elenco} role="listbox" className="campo-cerca-elenco">
          {mostrate.length === 0 && <li className="campo-cerca-vuoto">{nessuna}</li>}
          {mostrate.map((v, i) => (
            <Fragment key={v.valore}>
              {cercato.trim() === "" && v.gruppo && v.gruppo !== mostrate[i - 1]?.gruppo && (
                <li role="presentation" className="campo-cerca-gruppo">
                  {v.gruppo}
                </li>
              )}
            <li
              id={`${elenco}-${i}`}
              role="option"
              aria-selected={v.valore === valore}
              className={i === evidenza ? "evidenza" : undefined}
              // mousedown e non click: il click arriva dopo il blur, che
              // avrebbe gia' chiuso l'elenco.
              onMouseDown={(e) => {
                e.preventDefault();
                scegli(v);
              }}
              onMouseEnter={() => setEvidenza(i)}
            >
              <span>{v.testo}</span>
              {v.dettaglio && <span className="narrativa">{v.dettaglio}</span>}
            </li>
            </Fragment>
          ))}
          {trovate.length > quante && (
            <li className="campo-cerca-vuoto">
              e altre {trovate.length - quante}: continua a scrivere
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
